import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faTriangleExclamation, faLock } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgDialog from 'modules/Common/ui/CgDialog';
import { Form, useCgForm } from 'modules/Common/form/v2';
import { isHttpOrHttpsUrl } from 'modules/FreeSupplier/supplierPublicationContracts';
import {
  buildSupplierPublicPagePath,
  buildSupplierPublicPageUrl,
} from 'modules/FreeSupplier/routes';
import {
  useRegenerateSupplierPublicationSlug,
  useSupplierPublicationSettings,
  useUpdateSupplierPublication,
} from 'modules/FreeSupplier/useSupplierPublication';
import { useMockData } from '../state/MockDataContext';
import { installationNameById } from '../state/adapters';
import { SupplierPanelLoader } from '../components/SupplierLoadingStates';
import PortalPreviewCanvas from 'modules/PublicCatalog/studio/PortalPreviewCanvas';
import PortalStudioInspector from '../components/portalStudio/PortalStudioInspector';
import { INSPECTOR_TABS, firstTabWithError } from '../components/portalStudio/inspectorTabs';

// Supplier-facing management of the same CbamPortal configuration used by the
// paid operator surface: slug, optional password, profile/contact/labels, and
// supplier-revision consent/exclusions.
//
// LAYOUT: a two-pane "studio" — the live preview canvas is the dominant pane
// and every setting lives in a tabbed inspector rail (PortalStudioInspector).
// It replaces a stacked column of p-6 cards beside a 400px preview strip,
// which (a) rendered the responsive public page at its mobile breakpoint and
// (b) spent most of its space on padding. See PortalPreviewCanvas for the
// device-scaling mechanism.

const optionalHttpUrlField = z
  .string()
  .trim()
  .refine((value) => value === '' || isHttpOrHttpsUrl(value), {
    message: 'Enter a valid http(s) URL (or leave it blank)',
  });

// Exported for the co-located test (D-S9-10 pure-mapping precedent — mirrors
// ProposedFactorForm.jsx's draftToProposedFactorFormValues /
// proposedFactorFormValuesToPatch convention).
export const sharePublicationFormSchema = z
  .object({
    slug: z
      .string()
      .trim()
      .max(80, 'Keep it under 80 characters')
      .refine(
        (value) => value === '' || /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value),
        'Use lowercase letters, numbers, and single hyphens',
      )
      .refine((value) => value === '' || value.length >= 3, 'Use at least 3 characters'),
    enabled: z.boolean(),
    includeFutureRevisions: z.boolean(),
    passwordProtected: z.boolean(),
    passwordSet: z.boolean(),
    password: z.string().max(128, 'Keep it under 128 characters'),
    profileText: z.string().max(2000, 'Keep it under 2000 characters'),
    websiteUrl: optionalHttpUrlField,
    logoUrl: optionalHttpUrlField,
    contactEmail: z
      .string()
      .trim()
      .refine((value) => value === '' || z.string().email().safeParse(value).success, {
        message: 'Enter a valid email address (or leave it blank)',
      }),
    labelsText: z.string().max(500, 'Keep labels under 500 characters'),
    // Keyed by HEX-ENCODED seriesFingerprint, positively framed as "is this
    // included" — the wire contract inverts this to excludedSeriesFingerprints
    // on submit. Raw fingerprints must never be react-hook-form path segments:
    // RHF's stringToPath strips `"`, `'`, `|` and `]` from names, so a raw
    // pipe-delimited fingerprint registers under a DIFFERENT key than the one
    // reset() seeds — the registered key validates as undefined and silently
    // blocks submit. Hex is [0-9a-f] only, immune to path parsing.
    included: z.record(z.boolean()),
  })
  .superRefine((values, context) => {
    if (values.passwordProtected && !values.passwordSet && !values.password.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'Set a password or turn password protection off',
      });
    }
  });

export const seriesFingerprintToFormKey = (fingerprint) =>
  // Fixed-width hex per UTF-16 code unit: [0-9a-f] only, collision-free,
  // and dependency-free (TextEncoder is missing in the jsdom test env).
  Array.from(String(fingerprint))
    .map((char) => char.charCodeAt(0).toString(16).padStart(4, '0'))
    .join('');

export const settingsToShareFormValues = (settings) => {
  const currentSeries = settings?.currentSeries || [];
  return {
    slug: settings?.slug || '',
    enabled: !!settings?.enabled,
    includeFutureRevisions: settings?.includeFutureRevisions ?? false,
    passwordProtected: !!settings?.passwordSet,
    passwordSet: !!settings?.passwordSet,
    password: '',
    profileText: settings?.profileText || '',
    websiteUrl: settings?.websiteUrl || '',
    logoUrl: settings?.logoUrl || '',
    contactEmail: settings?.contactEmail || '',
    labelsText: (settings?.labels || []).map((label) => label.label).join(', '),
    included: Object.fromEntries(
      currentSeries.map((row) => [
        seriesFingerprintToFormKey(row.seriesFingerprint),
        !row.excluded,
      ]),
    ),
  };
};

export const shareFormValuesToUpdateRequest = (values, currentSeries = [], existingLabels = []) => {
  const fingerprintByFormKey = Object.fromEntries(
    currentSeries.map((row) => [
      seriesFingerprintToFormKey(row.seriesFingerprint),
      row.seriesFingerprint,
    ]),
  );
  const labelsByName = new Map(
    existingLabels.map((label) => [label.label.trim().toLowerCase(), label]),
  );
  const labels = Array.from(
    new Set(
      (values.labelsText || '')
        .split(/[,\n]/)
        .map((label) => label.trim())
        .filter(Boolean),
    ),
  ).map((label) => {
    const existing = labelsByName.get(label.toLowerCase());
    return {
      key:
        existing?.key ||
        label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 60) ||
        'label',
      label,
      tone: existing?.tone || 'neutral',
    };
  });

  const request = {
    version: 1,
    enabled: values.enabled,
    includeFutureRevisions: values.includeFutureRevisions,
    excludedSeriesFingerprints: Object.entries(values.included || {})
      .filter(([, included]) => !included)
      .map(([formKey]) => fingerprintByFormKey[formKey])
      .filter(Boolean),
    profileText: values.profileText?.trim() ? values.profileText.trim() : null,
    websiteUrl: values.websiteUrl?.trim() ? values.websiteUrl.trim() : null,
    logoUrl: values.logoUrl?.trim() ? values.logoUrl.trim() : null,
    contactEmail: values.contactEmail?.trim() ? values.contactEmail.trim() : null,
    labels,
  };
  if (values.slug?.trim()) request.slug = values.slug.trim();
  if (!values.passwordProtected) request.password = null;
  else if (values.password.trim()) request.password = values.password.trim();
  return request;
};

// Published state at a glance, carried in the page header so the inspector's
// master switch doesn't have to shout it.
function PortalStatusPill({ settings }) {
  const { t } = useTranslation('freeSupplier');
  const live = !!settings?.enabled;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
        live
          ? 'border-primaryColor/30 bg-primaryColor/10 text-primaryColor'
          : 'border-slate-200 bg-slate-50 text-slate-500'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-primaryColor' : 'bg-slate-400'}`} />
      {live
        ? t('freeSupplier:sharePortal.status.live', 'Live')
        : t('freeSupplier:sharePortal.status.offline', 'Offline')}
      {live && settings?.passwordSet ? (
        <FontAwesomeIcon
          icon={faLock}
          className="text-[10px]"
          title={t('freeSupplier:sharePortal.status.passwordProtected', 'Password protected')}
        />
      ) : null}
    </span>
  );
}

export default function SharePortalPage() {
  const { t } = useTranslation('freeSupplier');
  const { installationDocs } = useMockData();
  const settingsQuery = useSupplierPublicationSettings();
  const updateMutation = useUpdateSupplierPublication();
  const regenerateMutation = useRegenerateSupplierPublicationSlug();
  const settings = settingsQuery.data;

  const [regenerateOpen, setRegenerateOpen] = useState(false);
  // Bumped on a successful save and by the canvas's reload button — the
  // preview iframe is keyed on this (see `previewKey`) so it reloads with the
  // just-published content. A separate browsing context (iframe) can't be
  // refreshed by react-query invalidation; remounting via `key` is the refresh
  // mechanism.
  const [previewNonce, setPreviewNonce] = useState(0);
  // Inspector tab. `onInvalid` below switches to the offending tab so a
  // validation error can never hide behind an inactive one (which would read
  // as a dead Save button).
  const [activeTab, setActiveTab] = useState(INSPECTOR_TABS[0].id);

  const methods = useCgForm({
    schema: sharePublicationFormSchema,
    defaultValues: settingsToShareFormValues(undefined),
  });
  // Seed the form once the real settings land — RHF defaultValues are fixed
  // at mount, so a real fetch (as opposed to a modal opened on already-cached
  // data) needs an explicit reset once, mirroring MockDataContext's
  // seededShare ref pattern.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !settingsQuery.isSuccess) return;
    seededRef.current = true;
    methods.reset(settingsToShareFormValues(settings));
  }, [settingsQuery.isSuccess, settings, methods]);

  const onSubmit = (values) => {
    updateMutation.mutate(
      shareFormValuesToUpdateRequest(values, settings?.currentSeries || [], settings?.labels || []),
      {
        onSuccess: (savedSettings) => {
          methods.reset(settingsToShareFormValues(savedSettings));
          setPreviewNonce((n) => n + 1);
        },
      },
    );
  };

  const publicUrl = settings?.slug ? buildSupplierPublicPageUrl(settings.slug) : null;
  // Display-ready publication rows for the inspector's inclusion list: the
  // fingerprint→form-key mapping stays here beside the mapper that owns it (and
  // beside the schema that consumes those keys), and the installation lookup
  // stays with the docs it reads, so the panel is purely presentational.
  const seriesRows = (settings?.currentSeries || []).map((row) => ({
    ...row,
    formKey: seriesFingerprintToFormKey(row.seriesFingerprint),
    installationName: installationNameById(installationDocs, row.installationId),
  }));
  // Live preview only makes sense once the page is actually published — same
  // condition the header status pill reflects.
  const previewAvailable = !!(settings?.enabled && settings?.slug);
  const previewKey = `${settings?.slug || ''}-${previewNonce}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 m-0">
            {t('freeSupplier:sharePortal.title', 'CBAM Portal')}
          </h1>
          <p className="text-sm text-slate-500 mt-1 m-0">
            {t(
              'freeSupplier:sharePortal.subtitle',
              'One public catalog and widget for your company information and the emission-factor revisions you consent to share.',
            )}
          </p>
        </div>
        {settings ? (
          <div className="flex items-center gap-3">
            <PortalStatusPill settings={settings} />
            {/* Real viewCount + lastAccessedAt, never mocked. */}
            <span className="text-xs text-slate-400">
              {t(
                'freeSupplier:sharePortal.viewsLastViewed',
                '{{count}} views · last viewed {{date}}',
                {
                  count: settings.viewCount,
                  date: settings.lastAccessedAt
                    ? new Date(settings.lastAccessedAt).toLocaleDateString()
                    : t('freeSupplier:sharePortal.never', 'never'),
                },
              )}
            </span>
          </div>
        ) : null}
      </div>

      {settingsQuery.isLoading ? (
        <SupplierPanelLoader
          ariaLabel={t(
            'freeSupplier:sharePortal.loading.ariaLabel',
            'Loading your CBAM Portal settings',
          )}
          messages={[
            t('freeSupplier:sharePortal.loading.message1', 'Loading your portal settings'),
            t('freeSupplier:sharePortal.loading.message2', 'Checking your public link'),
            t(
              'freeSupplier:sharePortal.loading.message3',
              'Gathering the revisions you consented to share',
            ),
          ]}
          minHeight={260}
        />
      ) : settingsQuery.isError ? (
        // F3: a query ERROR must never fall through to the form below — that
        // form's defaultValues are the empty/disabled shape
        // (settingsToShareFormValues(undefined)), and seededRef only resets
        // it once isSuccess. Rendering the form on error would let Save
        // submit those blanks over the supplier's real (unfetched) settings.
        <div className="rounded-2xl border border-dashed border-red-300 p-10 text-center">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="text-red-300 text-2xl mb-3"
          />
          <p className="text-sm text-slate-600 m-0">
            {t(
              'freeSupplier:sharePortal.loadError.title',
              'We couldn’t load your CBAM Portal settings.',
            )}
          </p>
          <p className="text-xs text-slate-400 mt-1 m-0">
            {t('freeSupplier:sharePortal.loadError.hint', 'Check your connection and try again.')}
          </p>
          <CgRadixButton
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => settingsQuery.refetch()}
          >
            {t('freeSupplier:sharePortal.loadError.retry', 'Retry')}
          </CgRadixButton>
        </div>
      ) : (
        // The studio: canvas first (so it stays on top when the panes stack
        // below `xl`), inspector rail second. grid-cols-[minmax(0,1fr)] rather
        // than grid-cols-1 sidesteps the prebuilt CSS bundle's `.grid-cols-1`
        // loading after the JIT rule and overriding the `xl:` arbitrary-value
        // column split — same fix as EmbedWidgetPage's preview grid. The fixed
        // xl height is what lets both panes scroll independently instead of the
        // whole page growing to the taller one.
        <div className="grid grid-cols-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_360px] gap-4 xl:h-[calc(100vh-14rem)] xl:min-h-[600px]">
          <PortalPreviewCanvas
            url={publicUrl}
            src={previewAvailable ? buildSupplierPublicPagePath(settings.slug) : null}
            frameKey={previewKey}
            title={t('freeSupplier:sharePortal.previewTitle', 'Your CBAM Portal preview')}
            dirty={methods.formState.isDirty}
            onRefresh={() => setPreviewNonce((n) => n + 1)}
            emptyState={
              <div className="max-w-xs rounded-2xl border border-dashed border-slate-300 bg-white/80 px-6 py-8 text-center">
                <FontAwesomeIcon
                  icon={faGlobe}
                  className="text-slate-300 text-2xl mb-3"
                />
                <p className="text-sm text-slate-600 m-0">
                  {t(
                    'freeSupplier:sharePortal.emptyPreview.title',
                    "Your CBAM Portal isn't published yet.",
                  )}
                </p>
                <p className="text-xs text-slate-400 mt-1 m-0">
                  {t(
                    'freeSupplier:sharePortal.emptyPreview.hint',
                    'Turn it on under Publish and save — the live preview appears here.',
                  )}
                </p>
              </div>
            }
          />

          <Form
            methods={methods}
            onSubmit={onSubmit}
            onInvalid={(errors) => setActiveTab(firstTabWithError(errors))}
            className="rounded-2xl bg-white border border-slate-200 overflow-hidden flex flex-col"
          >
            <PortalStudioInspector
              settings={settings}
              publicUrl={publicUrl}
              seriesRows={seriesRows}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onRegenerate={() => setRegenerateOpen(true)}
              saving={updateMutation.isPending}
              saved={updateMutation.isSuccess}
            />
          </Form>
        </div>
      )}

      <CgDialog
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
        title={t('freeSupplier:sharePortal.regenerateDialog.title', 'Regenerate your link')}
        description={t(
          'freeSupplier:sharePortal.regenerateDialog.description',
          'The current link stops working immediately. Anyone using the old link will see a “not available” page.',
        )}
        footer={
          <div className="flex items-center justify-end gap-2">
            <CgRadixButton
              variant="ghost"
              onClick={() => setRegenerateOpen(false)}
              disabled={regenerateMutation.isPending}
            >
              {t('freeSupplier:sharePortal.regenerateDialog.cancel', 'Cancel')}
            </CgRadixButton>
            <CgRadixButton
              variant="danger"
              loading={regenerateMutation.isPending}
              onClick={async () => {
                const regeneratedSettings = await regenerateMutation.mutateAsync();
                methods.reset(settingsToShareFormValues(regeneratedSettings));
                setRegenerateOpen(false);
              }}
            >
              {t('freeSupplier:sharePortal.regenerateDialog.confirm', 'Regenerate')}
            </CgRadixButton>
          </div>
        }
      >
        <p className="text-sm text-slate-600 m-0">
          {t(
            'freeSupplier:sharePortal.regenerateDialog.body',
            "You'll need to share the new link with anyone who should see it.",
          )}
        </p>
      </CgDialog>
    </div>
  );
}
